import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import type {
  ExtractedTableRow,
  PdfTableRegion,
  PdfTableRegionCollection,
} from "../types";

// =============================================================================
// PDF Region Selector — React port of Shared/PdfRegionSelectorDialog.razor +
// wwwroot/js/pdfRegionSelector.js. Coordinate system identical: percentages
// 0-100 of canvas dimensions, scale-independent across zoom & resolution.
// =============================================================================

interface Props {
  open: boolean;
  file: File;
  existing?: PdfTableRegionCollection;
  onSave: (regions: PdfTableRegionCollection) => void;
  onClose: () => void;
}

type Handle = "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w";
const HANDLE_NAMES: Handle[] = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];
const HANDLE_SIZE = 10;
const MIN_REGION = 20;
const BORDER_COLOR = "#E0775A"; // coral primary
const HANDLE_COLOR = "#E0775A";
const OVERLAY_COLOR = "rgba(59, 47, 38, 0.45)"; // warm ink, soft

interface PixelRegion {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function PdfRegionSelectorDialog({
  open,
  file,
  existing,
  onSave,
  onClose,
}: Props) {
  // -- Server-rendered page state -------------------------------------------
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const [imgDims, setImgDims] = useState<{ w: number; h: number } | null>(null);
  const [zoomLevel, setZoomLevel] = useState(100); // 25..200, 25 steps
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // -- Regions state — percentages, by page --------------------------------
  const [regions, setRegions] = useState<Record<number, PdfTableRegion>>(
    () => (existing?.regions ? { ...existing.regions } : {}),
  );

  // -- Preview state --------------------------------------------------------
  const [preview, setPreview] = useState<ExtractedTableRow[] | null>(null);
  const [extracting, setExtracting] = useState(false);

  // -- Canvas refs ----------------------------------------------------------
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  // The region currently being drawn/edited (in pixel coords).
  const pixelRef = useRef<PixelRegion | null>(null);
  // Drag state
  const drawingRef = useRef(false);
  const draggingRef = useRef(false);
  const resizingRef = useRef<Handle | null>(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const fitScaleRef = useRef(1);
  const [, forceRender] = useState(0);
  const tick = useCallback(() => forceRender((n) => n + 1), []);

  // ---------------------------------------------------------------------------
  // Load page count + first page when the dialog opens.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setErrorMessage(null);

    (async () => {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await fetch("/api/pdf/page-count", {
          method: "POST",
          credentials: "same-origin",
          body: fd,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        setPageCount(data.pageCount);
        // Start on first page that has a region, if any.
        const firstWithRegion = existing?.regions
          ? Math.min(
              ...Object.keys(existing.regions).map((k) => parseInt(k, 10)),
            )
          : null;
        setCurrentPage(
          firstWithRegion && !Number.isNaN(firstWithRegion)
            ? firstWithRegion
            : 1,
        );
      } catch (err) {
        if (!cancelled) {
          setErrorMessage(
            `Failed to initialize: ${
              err instanceof Error ? err.message : String(err)
            }`,
          );
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, file, existing]);

  // ---------------------------------------------------------------------------
  // Load the current page image whenever currentPage or pageCount changes.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!open || pageCount === 0) return;
    let cancelled = false;
    setLoading(true);
    setErrorMessage(null);
    setImgSrc(null);
    setImgDims(null);

    (async () => {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("page", String(currentPage));
        fd.append("dpi", "150");
        const res = await fetch("/api/pdf/render-page", {
          method: "POST",
          credentials: "same-origin",
          body: fd,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        if (!data.success) {
          throw new Error(data.errorMessage ?? "Render failed");
        }
        const src = `data:image/png;base64,${data.base64Image}`;
        setImgSrc(src);
        setImgDims({ w: data.originalWidth, h: data.originalHeight });
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setErrorMessage(
            `Failed to load page: ${
              err instanceof Error ? err.message : String(err)
            }`,
          );
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, file, currentPage, pageCount]);

  // ---------------------------------------------------------------------------
  // When the image loads OR zoom changes, recompute canvas dims + redraw.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!imgSrc || !imgDims) return;
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      renderCanvas();
    };
    img.src = imgSrc;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgSrc, imgDims, zoomLevel]);

  // Recompute pixel region whenever existing regions or page change.
  useEffect(() => {
    const r = regions[currentPage];
    const canvas = canvasRef.current;
    if (!canvas || !r) {
      pixelRef.current = null;
      renderCanvas();
      return;
    }
    pixelRef.current = {
      x: (r.x / 100) * canvas.width,
      y: (r.y / 100) * canvas.height,
      width: (r.width / 100) * canvas.width,
      height: (r.height / 100) * canvas.height,
    };
    renderCanvas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regions, currentPage, imgSrc, zoomLevel]);

  // ---------------------------------------------------------------------------
  // Canvas drawing.
  // ---------------------------------------------------------------------------
  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    const container = containerRef.current;
    if (!canvas || !img || !container || !imgDims) return;

    // fitScale ensures the original-DPI image fits the container without
    // upscaling, then user-zoom multiplies that.
    const cw = container.clientWidth - 32;
    const ch = container.clientHeight - 32;
    const sx = cw / imgDims.w;
    const sy = ch / imgDims.h;
    const fitScale = Math.min(sx, sy, 1);
    const finalScale = fitScale * (zoomLevel / 100);
    fitScaleRef.current = finalScale;

    canvas.width = Math.floor(imgDims.w * finalScale);
    canvas.height = Math.floor(imgDims.h * finalScale);

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const r = pixelRef.current;
    if (r && (r.width !== 0 || r.height !== 0)) {
      const display = {
        x: r.width < 0 ? r.x + r.width : r.x,
        y: r.height < 0 ? r.y + r.height : r.y,
        width: Math.abs(r.width),
        height: Math.abs(r.height),
      };

      // Overlay outside selection
      ctx.fillStyle = OVERLAY_COLOR;
      ctx.fillRect(0, 0, canvas.width, display.y);
      ctx.fillRect(0, display.y, display.x, display.height);
      ctx.fillRect(
        display.x + display.width,
        display.y,
        canvas.width - display.x - display.width,
        display.height,
      );
      ctx.fillRect(
        0,
        display.y + display.height,
        canvas.width,
        canvas.height - display.y - display.height,
      );

      // Border
      ctx.strokeStyle = BORDER_COLOR;
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(display.x, display.y, display.width, display.height);
      ctx.setLineDash([]);

      // Handles
      if (!drawingRef.current) {
        ctx.fillStyle = HANDLE_COLOR;
        for (const p of handlePositions(display)) {
          ctx.fillRect(
            p.x - HANDLE_SIZE / 2,
            p.y - HANDLE_SIZE / 2,
            HANDLE_SIZE,
            HANDLE_SIZE,
          );
        }
      }
    }
  }, [imgDims, zoomLevel]);

  // ---------------------------------------------------------------------------
  // Mouse handlers.
  // ---------------------------------------------------------------------------
  const localCoords = (e: ReactMouseEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onMouseDown = (e: ReactMouseEvent<HTMLCanvasElement>) => {
    const { x, y } = localCoords(e);

    if (pixelRef.current) {
      const handle = handleAt(pixelRef.current, x, y);
      if (handle) {
        resizingRef.current = handle;
        dragOffsetRef.current = { x, y };
        tick();
        return;
      }
      if (insideRegion(pixelRef.current, x, y)) {
        draggingRef.current = true;
        dragOffsetRef.current = {
          x: x - pixelRef.current.x,
          y: y - pixelRef.current.y,
        };
        tick();
        return;
      }
    }

    drawingRef.current = true;
    pixelRef.current = { x, y, width: 0, height: 0 };
    dragOffsetRef.current = { x, y };
    renderCanvas();
  };

  const onMouseMove = (e: ReactMouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const { x, y } = localCoords(e);

    // Cursor feedback
    if (pixelRef.current) {
      const h = handleAt(pixelRef.current, x, y);
      if (h) {
        canvas.style.cursor = ({
          nw: "nw-resize",
          n: "n-resize",
          ne: "ne-resize",
          e: "e-resize",
          se: "se-resize",
          s: "s-resize",
          sw: "sw-resize",
          w: "w-resize",
        } as const)[h];
      } else if (insideRegion(pixelRef.current, x, y)) {
        canvas.style.cursor = "move";
      } else {
        canvas.style.cursor = "crosshair";
      }
    } else {
      canvas.style.cursor = "crosshair";
    }

    if (drawingRef.current && pixelRef.current) {
      pixelRef.current.width = x - dragOffsetRef.current.x;
      pixelRef.current.height = y - dragOffsetRef.current.y;
      renderCanvas();
    } else if (draggingRef.current && pixelRef.current) {
      pixelRef.current.x = x - dragOffsetRef.current.x;
      pixelRef.current.y = y - dragOffsetRef.current.y;
      constrain(pixelRef.current, canvas);
      renderCanvas();
    } else if (resizingRef.current && pixelRef.current) {
      resize(pixelRef.current, resizingRef.current, x, y);
      renderCanvas();
    }
  };

  const onMouseUp = () => {
    if (drawingRef.current || draggingRef.current || resizingRef.current) {
      normalize(pixelRef.current);
      commitPixelToPage();
    }
    drawingRef.current = false;
    draggingRef.current = false;
    resizingRef.current = null;
  };

  const commitPixelToPage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const p = pixelRef.current;
    if (!p || p.width <= 0 || p.height <= 0) {
      // selection too small → clear page
      setRegions((prev) => {
        const next = { ...prev };
        delete next[currentPage];
        return next;
      });
      pixelRef.current = null;
      renderCanvas();
      return;
    }
    const region: PdfTableRegion = {
      x: (p.x / canvas.width) * 100,
      y: (p.y / canvas.height) * 100,
      width: (p.width / canvas.width) * 100,
      height: (p.height / canvas.height) * 100,
      createdAt: new Date().toISOString(),
    };
    setRegions((prev) => ({ ...prev, [currentPage]: region }));
  };

  // ---------------------------------------------------------------------------
  // Toolbar actions
  // ---------------------------------------------------------------------------
  const hasCurrentRegion = !!regions[currentPage];

  const clearCurrentPage = () => {
    setRegions((prev) => {
      const next = { ...prev };
      delete next[currentPage];
      return next;
    });
    pixelRef.current = null;
    renderCanvas();
  };

  const applyToEmpty = () => {
    const r = regions[currentPage];
    if (!r) return;
    setRegions((prev) => {
      const next = { ...prev };
      for (let p = 1; p <= pageCount; p++) {
        if (!next[p]) next[p] = { ...r, createdAt: new Date().toISOString() };
      }
      return next;
    });
    toast.success(`Applied region to empty pages`);
  };

  const overrideAll = () => {
    const r = regions[currentPage];
    if (!r) return;
    if (!window.confirm(
      `Replace the region on all ${pageCount} pages with the current selection?`,
    )) {
      return;
    }
    const next: Record<number, PdfTableRegion> = {};
    for (let p = 1; p <= pageCount; p++) {
      next[p] = { ...r, createdAt: new Date().toISOString() };
    }
    setRegions(next);
    toast.success(`Overrode all ${pageCount} pages with current region`);
  };

  // ---------------------------------------------------------------------------
  // Preview
  // ---------------------------------------------------------------------------
  const previewExtraction = async () => {
    setExtracting(true);
    setPreview(null);
    setErrorMessage(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append(
        "regionsJson",
        JSON.stringify({ regions, totalPages: pageCount }),
      );
      const res = await fetch("/api/pdf/preview-region", {
        method: "POST",
        credentials: "same-origin",
        body: fd,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const rows: ExtractedTableRow[] = await res.json();
      setPreview(rows);
    } catch (err) {
      setErrorMessage(
        `Extraction failed: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    } finally {
      setExtracting(false);
    }
  };

  const copyTsv = async () => {
    if (!preview) return;
    const tsv = preview
      .map((row) =>
        row.cells
          .map((c) => (c ?? "").replace(/[\t\n\r]/g, " "))
          .join("\t"),
      )
      .join("\n");
    try {
      await navigator.clipboard.writeText(tsv);
      toast.success("Copied TSV to clipboard");
    } catch {
      toast.error("Clipboard not available");
    }
  };

  // ---------------------------------------------------------------------------
  // Save / close
  // ---------------------------------------------------------------------------
  const handleSave = () => {
    const count = Object.keys(regions).length;
    if (count === 0) {
      toast.warning("No regions defined");
      return;
    }
    onSave({ regions, totalPages: pageCount });
  };

  const definedPages = useMemo(
    () => Object.keys(regions).map((k) => parseInt(k, 10)).sort((a, b) => a - b),
    [regions],
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="xl"
      title={
        <span className="flex items-center gap-3">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-primary-soft text-primary-ink">
            <RegionGlyph />
          </span>
          <span>
            Select table region
            <span className="ml-2 font-normal text-ink-muted">· {file.name}</span>
          </span>
        </span>
      }
    >
      {/* Controls bar */}
      <div className="flex flex-wrap items-center gap-3 border-b border-rule bg-canvas-2/40 px-6 py-3">
        {/* Page nav */}
        <div className="inline-flex items-center gap-1 rounded-md border border-rule bg-surface p-0.5 shadow-soft">
          <ToolBtn
            disabled={currentPage <= 1 || loading}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            aria-label="Previous page"
          >
            <ChevronLeft />
          </ToolBtn>
          <span className="px-2 text-[0.85rem] font-medium text-ink">
            Page {currentPage}
            <span className="text-ink-muted"> / {pageCount || "—"}</span>
          </span>
          <ToolBtn
            disabled={currentPage >= pageCount || loading}
            onClick={() => setCurrentPage((p) => Math.min(pageCount, p + 1))}
            aria-label="Next page"
          >
            <ChevronRight />
          </ToolBtn>
        </div>

        {/* Zoom */}
        <div className="inline-flex items-center gap-1 rounded-md border border-rule bg-surface p-0.5 shadow-soft">
          <ToolBtn
            disabled={zoomLevel <= 25 || loading}
            onClick={() => setZoomLevel((z) => Math.max(25, z - 25))}
            aria-label="Zoom out"
          >
            <MinusGlyph />
          </ToolBtn>
          <span className="px-2 text-[0.85rem] font-medium tabular-nums text-ink">
            {zoomLevel}%
          </span>
          <ToolBtn
            disabled={zoomLevel >= 200 || loading}
            onClick={() => setZoomLevel((z) => Math.min(200, z + 25))}
            aria-label="Zoom in"
          >
            <PlusGlyph />
          </ToolBtn>
          <ToolBtn
            title="Reset to 100%"
            disabled={loading}
            onClick={() => setZoomLevel(100)}
            aria-label="Fit to 100%"
          >
            <FitGlyph />
          </ToolBtn>
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <PillBtn
            disabled={!hasCurrentRegion || loading}
            onClick={clearCurrentPage}
          >
            Clear
          </PillBtn>
          <PillBtn
            disabled={!hasCurrentRegion || loading || pageCount <= 1}
            onClick={applyToEmpty}
            tone="sage"
          >
            Apply to empty pages
          </PillBtn>
          <PillBtn
            disabled={!hasCurrentRegion || loading || pageCount <= 1}
            onClick={overrideAll}
            tone="danger"
          >
            Override all
          </PillBtn>
          <PillBtn
            disabled={definedPages.length === 0 || extracting || loading}
            onClick={previewExtraction}
            tone="primary"
          >
            {extracting ? "Extracting…" : "Preview"}
          </PillBtn>
        </div>
      </div>

      {/* Defined regions summary */}
      {definedPages.length > 0 && (
        <div className="flex items-center gap-2 border-b border-rule bg-sage-soft px-6 py-2.5 text-[0.85rem] text-sage-ink">
          <CheckCircleGlyph />
          <span className="font-medium">
            {definedPages.length} {definedPages.length === 1 ? "page" : "pages"} ready
          </span>
          <span className="text-ink-muted">·</span>
          <span className="text-ink-soft">
            {definedPages.map((p) => `Page ${p}`).join(", ")}
          </span>
        </div>
      )}

      {/* Canvas */}
      <div
        ref={containerRef}
        className="relative flex h-[60vh] flex-1 items-center justify-center overflow-auto bg-canvas-2/50 p-4"
      >
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-canvas/70 backdrop-blur-sm">
            <span className="rounded-full bg-surface px-4 py-2 text-[0.88rem] font-medium text-ink-soft shadow-soft">
              Loading PDF…
            </span>
          </div>
        )}
        {errorMessage && (
          <div className="absolute inset-x-6 top-6 z-10 rounded-md border border-danger/30 bg-danger-soft px-4 py-3 text-[0.88rem] text-ink">
            {errorMessage}
          </div>
        )}
        <canvas
          ref={canvasRef}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          className="cursor-crosshair rounded bg-surface shadow-soft"
        />
      </div>

      {/* Help text */}
      <div className="border-t border-rule bg-canvas-2/30 px-6 py-2.5 text-[0.85rem] text-ink-soft">
        {hasCurrentRegion
          ? "Drag the selection to move it, or grab a handle to resize. Click outside to start a new selection."
          : "Click and drag to select the table region containing objections."}
      </div>

      {/* Preview pane */}
      {preview && (
        <div className="max-h-[280px] overflow-y-auto border-t border-rule bg-surface px-6 py-4">
          {preview.length === 0 ? (
            <div className="flex items-start gap-3 rounded-md bg-butter-soft px-4 py-3 text-[0.88rem] text-butter-ink">
              <span aria-hidden className="mt-0.5">⚠</span>
              <span>
                No table data found in the selected region(s). Try adjusting
                the selection or check if the PDF contains actual table data
                (not just images).
              </span>
            </div>
          ) : (
            <>
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2 text-[0.9rem] font-semibold text-ink">
                  Extraction preview
                  <span className="rounded-full bg-primary-soft px-2 py-0.5 text-[0.74rem] font-medium text-primary-ink">
                    {preview.length} row{preview.length === 1 ? "" : "s"}
                  </span>
                  <span className="text-[0.78rem] text-ink-muted">
                    up to {Math.max(...preview.map((r) => r.cells.length))}{" "}
                    columns
                  </span>
                </div>
                <button
                  type="button"
                  onClick={copyTsv}
                  className="inline-flex items-center gap-1.5 rounded-md bg-canvas-2 px-2.5 py-1 text-[0.82rem] font-medium text-ink-soft hover:bg-surface-2 hover:text-ink"
                >
                  <CopyGlyph /> Copy TSV
                </button>
              </div>
              <div className="overflow-hidden rounded-md border border-rule">
                <table className="w-full font-mono text-[0.82rem]">
                  <thead className="bg-canvas-2">
                    <tr>
                      <th className="border-b border-r border-rule px-2.5 py-1.5 text-left text-[0.75rem] font-semibold uppercase tracking-wider text-ink-muted">
                        Pg
                      </th>
                      <th className="border-b border-r border-rule px-2.5 py-1.5 text-left text-[0.75rem] font-semibold uppercase tracking-wider text-ink-muted">
                        Row
                      </th>
                      {Array.from(
                        {
                          length: Math.max(
                            ...preview.map((r) => r.cells.length),
                          ),
                        },
                        (_, i) => (
                          <th
                            key={i}
                            className="border-b border-r border-rule px-2.5 py-1.5 text-left text-[0.75rem] font-semibold uppercase tracking-wider text-ink-muted"
                          >
                            Col {i + 1}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row, i) => (
                      <tr key={i} className="odd:bg-surface even:bg-canvas/30">
                        <td className="border-b border-r border-rule px-2.5 py-1.5 text-ink-muted">
                          {row.pageNumber}
                        </td>
                        <td className="border-b border-r border-rule px-2.5 py-1.5 text-ink-muted">
                          {row.rowIndex + 1}
                        </td>
                        {row.cells.map((c, j) => (
                          <td
                            key={j}
                            className="border-b border-r border-rule px-2.5 py-1.5"
                          >
                            {c?.trim() || (
                              <span className="text-ink-faint">—</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Footer */}
      <footer className="flex items-center justify-between gap-3 border-t border-rule bg-canvas-2/30 px-6 py-4">
        <div className="text-[0.85rem] text-ink-muted">
          {Object.keys(regions).length === 0 ? (
            "No regions defined yet"
          ) : (
            <>
              <span className="font-semibold text-ink">
                {Object.keys(regions).length}
              </span>{" "}
              region{Object.keys(regions).length === 1 ? "" : "s"} ready to save
            </>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Button intent="ghost" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            intent="primary"
            size="md"
            disabled={Object.keys(regions).length === 0}
            onClick={handleSave}
          >
            Save regions
          </Button>
        </div>
      </footer>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Toolbar pieces
// ---------------------------------------------------------------------------

function ToolBtn({
  children,
  onClick,
  disabled,
  title,
  ...rest
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  "aria-label"?: string;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className="flex h-8 w-8 items-center justify-center rounded-sm text-ink-soft transition-colors hover:bg-canvas-2 hover:text-ink disabled:opacity-30 disabled:hover:bg-transparent"
      {...rest}
    >
      {children}
    </button>
  );
}

function PillBtn({
  children,
  onClick,
  disabled,
  tone = "neutral",
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tone?: "neutral" | "primary" | "sage" | "danger";
}) {
  const palette = {
    neutral:
      "bg-surface text-ink-soft border-rule hover:border-rule-strong hover:bg-surface-2",
    primary:
      "bg-primary text-white border-primary shadow-soft hover:bg-primary-ink hover:shadow-lift",
    sage:
      "bg-sage-soft text-sage-ink border-sage/30 hover:bg-sage/20",
    danger:
      "bg-surface text-danger border-danger/30 hover:bg-danger-soft",
  }[tone];

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "rounded-md border px-3 py-1.5 text-[0.85rem] font-medium transition-all",
        "disabled:opacity-30 disabled:cursor-not-allowed",
        palette,
      )}
    >
      {children}
    </button>
  );
}

function RegionGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <rect x="2" y="2" width="10" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M5 5h4v4H5z" fill="currentColor" />
    </svg>
  );
}

function ChevronLeft() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path d="M9 3.5L5.5 7 9 10.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path d="M5 3.5L8.5 7 5 10.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PlusGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path d="M7 3v8M3 7h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function MinusGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path d="M3 7h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function FitGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path d="M3 5V3h2M11 5V3H9M3 9v2h2M11 9v2H9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function CheckCircleGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.4" />
      <path d="M4.5 7L6 8.5 9.5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CopyGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
      <rect x="3" y="3" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.3" />
      <path d="M2 8V2.5A.5.5 0 0 1 2.5 2H8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

// =============================================================================
// Geometry helpers (mirror of pdfRegionSelector.js)
// =============================================================================

function handlePositions(r: PixelRegion) {
  return [
    { x: r.x, y: r.y },
    { x: r.x + r.width / 2, y: r.y },
    { x: r.x + r.width, y: r.y },
    { x: r.x + r.width, y: r.y + r.height / 2 },
    { x: r.x + r.width, y: r.y + r.height },
    { x: r.x + r.width / 2, y: r.y + r.height },
    { x: r.x, y: r.y + r.height },
    { x: r.x, y: r.y + r.height / 2 },
  ];
}

function handleAt(r: PixelRegion, x: number, y: number): Handle | null {
  const hs = HANDLE_SIZE;
  const points = handlePositions(r);
  for (let i = 0; i < HANDLE_NAMES.length; i++) {
    if (Math.abs(x - points[i].x) <= hs && Math.abs(y - points[i].y) <= hs) {
      return HANDLE_NAMES[i];
    }
  }
  return null;
}

function insideRegion(r: PixelRegion, x: number, y: number) {
  return (
    x >= r.x && x <= r.x + r.width && y >= r.y && y <= r.y + r.height
  );
}

function constrain(r: PixelRegion, canvas: HTMLCanvasElement) {
  r.x = Math.max(0, Math.min(r.x, canvas.width - r.width));
  r.y = Math.max(0, Math.min(r.y, canvas.height - r.height));
}

function resize(r: PixelRegion, handle: Handle, x: number, y: number) {
  if (handle.includes("n")) {
    const newHeight = r.y + r.height - y;
    r.y = y;
    r.height = newHeight;
  }
  if (handle.includes("s")) {
    r.height = y - r.y;
  }
  if (handle.includes("w")) {
    const newWidth = r.x + r.width - x;
    r.x = x;
    r.width = newWidth;
  }
  if (handle.includes("e")) {
    r.width = x - r.x;
  }
}

function normalize(r: PixelRegion | null) {
  if (!r) return;
  if (r.width < 0) {
    r.x += r.width;
    r.width = Math.abs(r.width);
  }
  if (r.height < 0) {
    r.y += r.height;
    r.height = Math.abs(r.height);
  }
  if (r.width < MIN_REGION || r.height < MIN_REGION) {
    r.x = 0;
    r.y = 0;
    r.width = 0;
    r.height = 0;
  }
}

